import { Injectable, inject, computed } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { filter, map, startWith } from "rxjs";
import { HelpService } from "src/app/features/help/help.service";

/**
 * Lightweight service for JAX navigation state management.
 * Uses Angular Router with a named outlet for lazy loading.
 * This service has NO AI SDK dependencies and can be safely imported
 * in eagerly-loaded components without bloating the initial bundle.
 *
 * For AI functionality, use JaxService (which is lazy-loaded via the route).
 */
@Injectable({
  providedIn: "root",
})
export class JaxStateService {
  private router = inject(Router);
  private helpService = inject(HelpService);

  // Reactive signal of the current URL, updated on navigation
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
  );

  /**
   * Signal indicating if the right-side panel (assistant or help) is open.
   * Drives the page-shift CSS in the main layout.
   */
  isOpen = computed(() => this.currentUrl()?.includes("(jax:") ?? false);

  /** Whether the Jax assistant specifically is showing. */
  isAssistantOpen = computed(
    () => this.currentUrl()?.includes("(jax:assistant") ?? false,
  );

  /** Whether the help panel (categories or a guide) is showing. */
  isHelpOpen = computed(
    () => this.currentUrl()?.includes("(jax:help") ?? false,
  );

  /** Open the Jax assistant. Replaces help if it was showing. */
  open(): void {
    this.router.navigate([{ outlets: { jax: "assistant" } }]);
  }

  /**
   * Open the help panel. If the user was last viewing a guide in this window,
   * reopen it; otherwise show the categories list. Replaces assistant if it
   * was showing.
   */
  openHelp(): void {
    const slug = this.helpService.lastSlug();
    this.router.navigate([
      { outlets: { jax: slug ? ["help", slug] : ["help"] } },
    ]);
  }

  /** Close the right-side panel entirely. */
  close(): void {
    this.router.navigate([{ outlets: { jax: null } }]);
  }

  /**
   * Toggle the Jax assistant. If assistant is showing → close. Otherwise
   * open assistant (which replaces help if help was open).
   */
  toggle(): void {
    if (this.isAssistantOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Toggle the help panel. If help is showing → close. Otherwise open help
   * (which replaces assistant if assistant was open).
   */
  toggleHelp(): void {
    if (this.isHelpOpen()) {
      this.close();
    } else {
      this.openHelp();
    }
  }
}
